import {
  surfacePalette,
  SurfacePaletteFullError,
  type SurfaceLayer,
} from '@/terrain/TerrainSurface';
import { stockTextures, type StockTextureDefinition } from './StockCatalog';

/**
 * Taking a material out of the shipped catalog and putting it on this site's palette.
 *
 * The catalog half of this ({@link StockCatalog}) is pure metadata and costs nothing to hold. This
 * half is where the pixels and the palette are, and the split is the same one `PropCatalog` and
 * `PropLibrary` already draw: a picker can list, search and describe every material without a single
 * byte of image being fetched, and the fetch happens once, when someone actually takes one.
 *
 * ## Why the swatch is inlined on the way in
 *
 * A catalog entry points at a hashed asset URL; a palette layer holds a `data:` URL. Converting
 * between them looks like pure overhead until you ask what happens to a saved project. A document
 * that stored `/assets/bluestone-flag-a3f9.png` would render correctly today and be a set of broken
 * links after the next deploy changed the hash — and it would not open at all outside the app, which
 * is the property the whole `SurfaceSwatch` design exists to preserve. So a stock layer carries its
 * own pixels exactly like a forged or uploaded one, and nothing downstream can tell the difference.
 *
 * `origin.kind: 'stock'` records where it came from anyway, because that *is* recoverable
 * information and throwing it away would be a choice. It is what lets the panel say "Bluestone flag,
 * from the library" rather than "uploaded", and it is the hook a future de-duplicating writer would
 * need to store a stock layer by reference instead of by value.
 */

/**
 * The bundler's view of `src/assets/swatches`.
 *
 * `import.meta.glob` over URLs only, eagerly: it emits every PNG as a content-addressed asset and
 * hands back the hashed path, so the catalog's swatches are cacheable and none of them is decoded —
 * or even downloaded — at start-up. The same technique as `bundledPropUrl`.
 */
const bundledSwatches = import.meta.glob('../assets/swatches/*.png', {
  query: '?url',
  import: 'default',
  eager: true,
});

/** Resolve a catalog `file` name to a URL the fetcher can use, or undefined when it was not bundled. */
export function bundledSwatchUrl(file: string): string | undefined {
  // Checked at runtime rather than asserted: `import.meta.glob` is typed as `unknown` values, and a
  // catalog naming a file the bundler never saw should fail with a message that says which file —
  // not throw a cast error somewhere further down.
  const url = bundledSwatches[`../assets/swatches/${file}`];
  return typeof url === 'string' ? url : undefined;
}

export class StockTextureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StockTextureError';
  }
}

/** Fetches a swatch URL and returns it as a `data:image/png;base64,…` string. Injected for tests. */
export type SwatchFetcher = (url: string) => Promise<string>;

export interface InstantiateStockOptions {
  /** Overrides the bundler's URL resolution. Injected for tests. */
  resolveUrl?: (file: string) => string | undefined;
  fetchImpl?: SwatchFetcher;
  signal?: AbortSignal;
}

/**
 * Put a catalog material on the palette and return the layer.
 *
 * Idempotent by id: taking the same material twice returns the layer already there rather than
 * minting `bluestone-flag-2`. That is not just tidiness — a second copy would occupy a second splat
 * channel out of sixteen and paint identically, so there is no state in which a user wanted it.
 *
 * Throws {@link SurfacePaletteFullError} when every channel is spoken for, matching `forgeTexture`,
 * and {@link StockTextureError} for an unknown id or a swatch that would not load. A stock material
 * whose pixels cannot be fetched is *not* degraded to a flat colour the way a forged one is: the
 * forge degrades because the alternative is losing a material the user described and cannot easily
 * re-describe, whereas this one is sitting in the catalog and the honest answer is "try again".
 */
export async function instantiateStockTexture(
  id: string,
  options: InstantiateStockOptions = {},
): Promise<SurfaceLayer> {
  const texture = stockTextures.get(id);
  if (!texture) {
    throw new StockTextureError(`No ground material named “${id}” in the library.`);
  }

  const existing = surfacePalette.find(texture.id);
  if (existing) return existing;

  if (surfacePalette.freeSlots <= 0) {
    throw new SurfacePaletteFullError(
      'This site has no room for another painted material. Remove one from the ground panel first.',
    );
  }

  const dataUrl = await loadStockSwatch(texture, options);

  return surfacePalette.add({
    // Asked for by id so the palette entry matches the catalog entry. `add` will still suffix it if
    // something else has taken the name, which is the one case where a stock layer and its catalog
    // record can legitimately drift — and the origin below is what records the true source anyway.
    id: texture.id,
    name: texture.name,
    color: texture.color,
    roughness: texture.roughness,
    plantable: texture.plantable,
    hint: texture.hint,
    character: texture.character,
    swatch: { dataUrl, tileMeters: texture.tileMeters },
    origin: {
      kind: 'stock',
      request: texture.id,
      grounding: texture.grounding,
      createdAt: new Date().toISOString(),
    },
  });
}

/** Fetch a catalog material's swatch as a data URL, without touching the palette. */
export async function loadStockSwatch(
  texture: StockTextureDefinition,
  options: InstantiateStockOptions = {},
): Promise<string> {
  const resolve = options.resolveUrl ?? bundledSwatchUrl;
  const url = resolve(texture.file);
  if (!url) {
    throw new StockTextureError(
      `The swatch for “${texture.name}” (${texture.file}) is not in this build.`,
    );
  }

  const fetcher = options.fetchImpl ?? defaultFetcher(options.signal);
  try {
    return await fetcher(url);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    const detail = error instanceof Error ? error.message : 'unknown error';
    throw new StockTextureError(`Could not load the swatch for “${texture.name}” (${detail}).`);
  }
}

/**
 * Fetch bytes and base64 them, with no canvas involved.
 *
 * Deliberately not routed through `normalizeSwatch`. That function exists to make an arbitrary
 * upload safe — square it, resample it to `SWATCH_TEXELS`, close its seam — and every one of those
 * steps is a lossy no-op here: catalog swatches are emitted at exactly the right size and are
 * tileable by construction, so re-encoding them through a canvas would cost a generation of quality
 * and a decode to change nothing. It also means this path works headless, which the canvas path
 * does not.
 */
function defaultFetcher(signal?: AbortSignal): SwatchFetcher {
  return async (url: string) => {
    const response = await fetch(url, signal ? { signal } : {});
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const buffer = await response.arrayBuffer();
    return `data:image/png;base64,${base64(new Uint8Array(buffer))}`;
  };
}

/** Chunked so a 100 KB swatch does not blow the argument limit on `String.fromCharCode`. */
function base64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
